import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLazyQuery } from '@apollo/client'
import { CircularProgress, ClickAwayListener, Grid } from '@mui/material'
import { useDebounce } from 'use-debounce'
import {
  DebouncedInlineSearch,
  type InlineSearchHandle,
} from '@pbt/pbt-ui-components'

import type {
  ClinicContactsSearchInput,
  ClinicSearchContactsPage,
  ContactSearchResult,
} from '~/api/graphql/generated/types'
import { SEARCH_CLINIC_CONTACTS_BY_NAME_QUERY } from '~/api/graphql/queries'
import Typography from '~/components/elements/Typography/Typography'

import ClinicSearchCard from './ClinicSearchCard'

import styles from './ClinicSearch.module.scss'

export interface ClinicSearchProps {
  debounceMs?: number
  disabled?: boolean
  minSearchLength?: number
  onAddNewPractice?: () => void
  onResultsVisibilityChange?: (visible: boolean) => void
  onSelectContact: (contact: ContactSearchResult) => void
  searchOnType?: boolean
}

interface SearchClinicContactsResponse {
  searchClinicContacts: ClinicSearchContactsPage
}

interface SearchClinicContactsByNameVariables {
  input: ClinicContactsSearchInput
}

const DEFAULT_DEBOUNCE_MS = 500
const DEFAULT_MIN_SEARCH_LENGTH = 3
const DEFAULT_PAGE_SIZE = 50

const ClinicSearch: React.FC<ClinicSearchProps> = ({
  onSelectContact,
  onAddNewPractice,
  onResultsVisibilityChange,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  minSearchLength = DEFAULT_MIN_SEARCH_LENGTH,
  disabled = false,
  searchOnType = true,
}) => {
  const { t } = useTranslation('TimeTable')
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearchValue] = useDebounce(searchValue, debounceMs)
  const [isOpen, setIsOpen] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const searchInputRef = useRef<InlineSearchHandle>(null)
  const searchContainerRef = useRef<HTMLDivElement>(null)

  const [searchClinicContacts, { data, loading }] = useLazyQuery<
    SearchClinicContactsResponse,
    SearchClinicContactsByNameVariables
  >(SEARCH_CLINIC_CONTACTS_BY_NAME_QUERY, {
    fetchPolicy: 'network-only',
  })

  const contacts = data?.searchClinicContacts?.data ?? []
  const showResults = isOpen && (contacts.length > 0 || loading || hasSearched)
  const showNoResults =
    isOpen && !loading && hasSearched && contacts.length === 0
  const showAddNewPractice = isOpen && onAddNewPractice

  useEffect(() => {
    onResultsVisibilityChange?.(showResults)
  }, [showResults, onResultsVisibilityChange])

  const executeSearch = (query: string) => {
    searchClinicContacts({
      variables: {
        input: {
          query,
          offset: 0,
          limit: DEFAULT_PAGE_SIZE,
        },
      },
    })
    setHasSearched(true)
    setIsOpen(true)
  }

  useEffect(() => {
    if (!searchOnType) {
      return
    }

    const trimmedValue = debouncedSearchValue.trim()
    if (trimmedValue.length >= minSearchLength) {
      executeSearch(trimmedValue)
    } else {
      setIsOpen(false)
      setHasSearched(false)
    }
  }, [debouncedSearchValue, minSearchLength, searchOnType])

  const handleInputChange = useCallback(
    (value: string) => {
      setSearchValue(value)
      if (searchOnType && value.trim().length >= minSearchLength) {
        setIsOpen(true)
      } else if (!searchOnType) {
        // Don't auto-open when searchOnType is disabled
      } else {
        setIsOpen(false)
        setHasSearched(false)
      }
    },
    [minSearchLength, searchOnType],
  )

  const handleSearchSubmit = useCallback(() => {
    const trimmedValue = searchValue.trim()
    if (!searchOnType && trimmedValue.length >= minSearchLength) {
      executeSearch(trimmedValue)
    }
  }, [searchOnType, searchValue, minSearchLength])

  const handleSelectContact = useCallback(
    (contact: ContactSearchResult) => {
      onSelectContact(contact)
      setIsOpen(false)
      setSearchValue('')
    },
    [onSelectContact],
  )

  // Set accessibility attributes and keyboard handler on the input element
  useEffect(() => {
    const container = searchContainerRef.current
    if (!container) {
      return undefined
    }

    const input = container.querySelector('input')
    if (!input) {
      return undefined
    }

    // Prevent browser autocomplete from interfering
    input.setAttribute('autocomplete', 'off')
    input.setAttribute('aria-expanded', String(isOpen))

    // Handle Enter key for search-on-submit mode
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !searchOnType) {
        event.preventDefault()
        handleSearchSubmit()
      }
    }

    input.addEventListener('keydown', handleKeyDown)
    return () => {
      input.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, searchOnType, handleSearchSubmit])

  const handleSearchFocus = () => {
    if (searchValue.trim().length >= minSearchLength) {
      setIsOpen(true)
    }
  }

  const handleClickAway = () => {
    setIsOpen(false)
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Grid container className={styles.clinicSearch} direction="column">
        <Grid item mb={1}>
          <Typography.Paragraph>
            {t('PMR_CONSENT.CLINIC_SEARCH_LABEL')}
          </Typography.Paragraph>
        </Grid>
        <Grid item ref={searchContainerRef}>
          <DebouncedInlineSearch
            classes={{ root: styles.searchInput }}
            disabled={disabled}
            ref={searchInputRef}
            showCloseButton={Boolean(searchValue)}
            onChange={handleInputChange}
            onFocus={handleSearchFocus}
          />
        </Grid>
        {showResults && (
          <Grid container className={styles.resultsContainerWrapper}>
            <Grid
              container
              item
              aria-label={String(t('PMR_CONSENT.CLINIC_SEARCH_RESULTS'))}
              className={styles.resultsContainer}
              role="listbox"
            >
              {loading && (
                <Grid item className={styles.loadingContainer}>
                  <CircularProgress size={24} />
                </Grid>
              )}
              {!loading &&
                contacts.length > 0 &&
                contacts.map((contact: ContactSearchResult) => (
                  <Grid item key={contact.kyriosId} width="100%">
                    <ClinicSearchCard
                      contact={contact}
                      onClick={() => handleSelectContact(contact)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          handleSelectContact(contact)
                        }
                      }}
                    />
                  </Grid>
                ))}
              {showNoResults && (
                <Grid item className={styles.noResults}>
                  <Typography.Paragraph>
                    {t('PMR_CONSENT.CLINIC_SEARCH_NO_RESULTS')}
                  </Typography.Paragraph>
                </Grid>
              )}
              {showAddNewPractice && (
                <Grid
                  item
                  className={styles.addNewPracticeContainer}
                  role="button"
                  tabIndex={0}
                  onClick={onAddNewPractice}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onAddNewPractice?.()
                    }
                  }}
                >
                  <Typography.Paragraph>
                    {t('PMR_CONSENT.CLINIC_SEARCH_DONT_SEE_PRACTICE')}{' '}
                    <span style={{ textDecoration: 'underline' }}>
                      {t('PMR_CONSENT.CLINIC_SEARCH_ADD_NEW_PRACTICE')}
                    </span>
                  </Typography.Paragraph>
                </Grid>
              )}
            </Grid>
          </Grid>
        )}
      </Grid>
    </ClickAwayListener>
  )
}

export default ClinicSearch
